import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Star, Gift, Sparkles, ChevronRight, Trophy, Share2, Award } from "lucide-react";
import { PAGE_NAME } from "../../types";
import useThemeHook from "../hooks/useThemeHook";
import { THEME_COLORS } from "../themes/DarkLight";
import { useTranslationHook } from "../hooks/useTranslationHook";
import arthereumProvider from "../../backend/ArthereumProvider";
import { useSelector } from "react-redux";

interface ContestPointsModalProps {
  closeModal: () => void;
  modalData: {
    pointsEarned: number;
    totalPoints: number;
    action: "grant" | "ad" | "login";
    isInContest: boolean;
    giveawayMessage?: string;
  };
}

const ContestPointsModal: React.FC<ContestPointsModalProps> = ({
  closeModal,
  modalData,
}) => {
  const navigate = useNavigate();
  const theme = useThemeHook();
  const getTranslationText = useTranslationHook();
  const userData = useSelector((state: any) => state?.actualUser);

  const [isSharing, setIsSharing] = React.useState(false);

  const actionLabels: Record<string, string> = {
    grant: getTranslationText("contest_action_grant"),
    ad: getTranslationText("contest_action_ad"),
    login: getTranslationText("contest_action_login"),
  };

  const handleGoToRewards = () => {
    closeModal();
    setTimeout(() => {
      navigate(PAGE_NAME.REWARDS);
    }, 200);
  };

  const handleShare = async () => {
    if (isSharing || !userData?.user?.id) return;
    setIsSharing(true);
    try {
      const isGrant = modalData.action === "grant";
      
      const shareText = isGrant 
        ? `I just claimed my free Arthereum grant! Join me on Arthereum Wallet. My code: ${userData.user.username}`
        : `I'm competing in the Arthereum Daily Contest! Join me and win Pi. My code: ${userData.user.username}`;
      
      if (navigator.share) {
        await navigator.share({
          title: "Arthereum Wallet",
          text: shareText,
          url: window.location.origin,
        });
      }
    } catch (error) {
      console.error("Error sharing:", error);
    } finally {
      setIsSharing(false);
      closeModal();
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-4 text-center">
      {/* Animated Stars Background */}
      <div className="relative mb-4">
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
          className="w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg shadow-orange-200"
        >
          <Star className="w-10 h-10 text-white fill-white" />
        </motion.div>
        
        {/* Floating sparkles */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="absolute -top-2 -right-2"
        >
          <Sparkles className="w-6 h-6 text-amber-400" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="absolute -bottom-1 -left-3"
        >
          <Sparkles className="w-5 h-5 text-orange-400" />
        </motion.div>
      </div>

      {/* Points Earned */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-2"
      >
        <div
          className="text-4xl font-bold bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent"
        >
          {getTranslationText("contest_points_earned", { 
            points: modalData.pointsEarned, 
            pointsLabel: modalData.pointsEarned === 1 ? getTranslationText("contest_point") : getTranslationText("contest_points") 
          })}
        </div>
      </motion.div>

      {/* Action Label */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        style={{ color: THEME_COLORS[theme].gray }}
        className="text-sm mb-4"
      >
        {getTranslationText("contest_earned_for", { action: actionLabels[modalData.action] })}
      </motion.p>

      {modalData.giveawayMessage && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.35 }}
          className="w-full p-3 rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-600 text-xs font-black uppercase tracking-wider mb-4"
        >
          ❄️ {modalData.giveawayMessage}
        </motion.div>
      )}

      {modalData.isInContest ? (
        <>
          {/* Total Points */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl p-4 mb-4 w-full"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-purple-600" />
                <span className="text-purple-800 font-medium">{getTranslationText("contest_your_points")}</span>
              </div>
              <div className="text-2xl font-bold text-purple-700">
                {modalData.totalPoints}
              </div>
            </div>
          </motion.div>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            style={{ color: THEME_COLORS[theme].inverse_theme }}
            className="text-sm mb-4"
          >
            {getTranslationText("contest_keep_earning")} <span className="font-bold text-purple-600">{getTranslationText("contest_prize_amount")}</span>!
          </motion.p>
        </>
      ) : (
        <>
          {/* Not in contest message */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4 mb-4 w-full"
          >
            <div className="flex items-center gap-2 mb-2">
              <Gift className="w-5 h-5 text-amber-600" />
              <span className="text-amber-800 font-semibold">{getTranslationText("contest_join_today")}</span>
            </div>
            <p className="text-amber-700 text-sm">
              {getTranslationText("contest_join_to_collect")}
            </p>
          </motion.div>
        </>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col gap-3 w-full">
        {(modalData.action === "grant" || modalData.action === "login") && (
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleShare}
            disabled={isSharing}
            className="w-full py-3 bg-white border-2 border-purple-100 text-purple-600 font-bold rounded-xl flex items-center justify-center gap-2 hover:bg-purple-50 transition-colors"
          >
            <Share2 className="w-5 h-5" />
            Share your win
          </motion.button>
        )}

        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleGoToRewards}
          className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-purple-200"
        >
          <Gift className="w-5 h-5" />
          {modalData.isInContest ? getTranslationText("contest_view_contest") : getTranslationText("contest_join_contest")}
          <ChevronRight className="w-4 h-4" />
        </motion.button>
      </div>
    </div>
  );
};

export default ContestPointsModal;
